package org.example.project.service

import org.example.project.manager.NodeScope

import androidx.compose.runtime.mutableStateMapOf
import kotlinx.serialization.decodeFromString
import kotlinx.serialization.json.Json
import org.example.project.dashboard.ScreenManager
import org.example.project.manager.LumaticaApiManager
import org.example.project.utils.All

private val rollenServiceJson = Json {
    ignoreUnknownKeys = true
    encodeDefaults = false
}

private fun rollenServiceApiPart(value: String): String =
    js("encodeURIComponent")(if (value.isBlank()) "~" else value) as String

private suspend fun loadRollenForCurrentUser(): List<All> {
    val userId = LoginService.getId()
    val firmenId = LoginService.getUser()?.firmenId.orEmpty().trim()
    if (userId.isBlank()) return emptyList()
    val raw = runCatching {
        LumaticaApiManager.call(
            "rollenverwaltung/user/${rollenServiceApiPart(userId)}/${rollenServiceApiPart(firmenId)}"
        )
    }.getOrDefault("")
    if (raw.isBlank()) return emptyList()
    return runCatching { rollenServiceJson.decodeFromString<List<All>>(raw) }.getOrDefault(emptyList())
}

object RollenService {
    const val KEINE = "KEINE"
    const val LESEN = "LESEN"
    const val SCHREIBEN = "SCHREIBEN"
    const val ALLES = "ALLES"

    const val ACTION_READ = "READ"
    const val ACTION_WRITE = "WRITE"
    const val ACTION_EDIT = "EDIT"
    const val ACTION_SAVE = "SAVE"
    const val ACTION_CREATE = "CREATE"
    const val ACTION_DELETE = "DELETE"
    const val ACTION_IMPORT = "IMPORT"
    const val ACTION_EXPORT = "EXPORT"
    const val ACTION_SETTINGS = "SETTINGS"
    const val ACTION_INSTALL = "INSTALL"
    const val ACTION_CANCEL = "CANCEL"
    const val ACTION_PASSWORD = "PASSWORD"
    const val ACTION_APPROVE = "APPROVE"
    const val ACTION_GENERATE = "GENERATE"

    private val readOnlyActions = setOf(ACTION_READ, ACTION_EXPORT, ACTION_SETTINGS)
    private val fullOnlyActions = setOf(ACTION_DELETE, ACTION_CANCEL, ACTION_PASSWORD)

    private val currentBerechtigungen = mutableStateMapOf<String, String>()
    private var rolleGeladen = false
    private var netzwerkVollzugriff = false
    private var currentRolleId: String = ""
    private var currentKarteiSeiten: Map<String, String> = emptyMap()

    private val personalChildScreens = setOf(
        "Arbeitszeitverwaltung",
        "Buchungsjournal",
        "Personalverwaltung",
        "MitarbeiterKalender",
        "MitarbeiterMeinUrlaub",
        "MitarbeiterZeitkonto",
        "Urlaubsuebersicht",
        "Zeitverwaltung",
        "MobileAnsichten"
    )

    private val inheritedScreenPermissions = mapOf(
        "SYSVertriebsDashboard" to "VertriebsDashboard",
        "MeineLeads" to "Vertriebspool",
        "LinkedInVertriebsindex" to "Vertriebsindex",
        "LinkedInChat" to "Vertriebsindex",
        "LinkedInFlow" to "VertriebsDashboard",
        "Konkurrenzindex" to "VertriebsDashboard",
        "AkquiseEinstellungen" to "VertriebsDashboard",
        "AufgabenViewer" to "MitarbeiterKalender"
    )

    private fun permissionForNewScreen(rolle: All, screenKey: String): String {
        inheritedScreenPermissions[screenKey]?.let { parentKey ->
            rolle.screenBerechtigungen[parentKey]?.let { return it }
        }
        if (screenKey in personalChildScreens) {
            rolle.screenBerechtigungen["Arbeitszeitverwaltung"]?.let { return it }
        }
        val assigned = rolle.screenBerechtigungen.values.filter { it != KEINE }
        val uniform = assigned.distinct().singleOrNull()
        return uniform ?: KEINE
    }

    private fun roleWithCurrentScreens(rolle: All, art: String): All {
        val node = when (art) {
            NodeScope.SYS, NodeScope.CUS, NodeScope.NET -> art
            else -> return rolle
        }
        val missing = ScreenManager.roleScreenKeys(node).filterNot { it in rolle.screenBerechtigungen }
        if (missing.isEmpty()) return rolle
        return rolle.copy(
            screenBerechtigungen = rolle.screenBerechtigungen + missing.associateWith { permissionForNewScreen(rolle, it) }
        )
    }

    fun supportedActions(screenKey: String): Set<String> =
        ScreenManager.supportedActionsForKey(screenKey)

    private fun getPermission(screenKey: String): String {
        if (netzwerkVollzugriff) return ALLES
        if (!rolleGeladen) return KEINE
        return currentBerechtigungen[screenKey]
            ?: if (screenKey == "WebseitenDashboard" || screenKey == "WebseitenWiederkehrer")
                currentBerechtigungen["WebseitenUebersicht"] ?: KEINE
            else if (screenKey == "AkquiseEinstellungen")
                currentBerechtigungen["LinkedInFlow"] ?: KEINE
            else KEINE
    }

    fun hasActionPermission(screenKey: String, action: String): Boolean =
        actionAllowedByPermission(screenKey, action, getPermission(screenKey))

    private fun actionAllowedByPermission(screenKey: String, action: String, permission: String): Boolean {
        if (action !in supportedActions(screenKey)) return false
        return when (permission) {
            KEINE -> false
            LESEN -> action in readOnlyActions || action == ACTION_READ
            SCHREIBEN -> action !in fullOnlyActions
            ALLES -> true
            else -> false
        }
    }

    fun isPageVisible(screenKey: String, pageId: String): Boolean {
        if (netzwerkVollzugriff) return true
        if (currentKarteiSeiten.isEmpty()) return true
        return currentKarteiSeiten["${screenKey}::${pageId}"] != "false"
    }

    fun loadCurrentUserRole(rolle: All?) {
        netzwerkVollzugriff = false
        currentBerechtigungen.clear()
        if (rolle == null || rolle.id.isBlank()) {
            rolleGeladen = false
            currentRolleId = ""
            currentKarteiSeiten = emptyMap()
            return
        }
        currentBerechtigungen.putAll(rolle.screenBerechtigungen)
        currentRolleId = rolle.id
        currentKarteiSeiten = rolle.karteiSeiten
        rolleGeladen = true
    }

    fun hasReadPermission(screenKey: String): Boolean =
        hasActionPermission(screenKey, ACTION_READ)

    fun hasWritePermission(screenKey: String): Boolean =
        hasActionPermission(screenKey, ACTION_WRITE)

    suspend fun refreshCurrentUserRole(art: String) {
        val rolleId = LoginService.getUser()?.rolle.orEmpty()
        val resolvedArt = when (art) {
            NodeScope.SYS, NodeScope.CUS, NodeScope.NET -> art
            else -> return
        }
        if (resolvedArt == NodeScope.NET) {
            netzwerkVollzugriff = true
            rolleGeladen = true
            currentBerechtigungen.clear()
            currentRolleId = ""
            currentKarteiSeiten = emptyMap()
            org.example.project.service.GlobalManager.triggerSessionConfigReload()
            return
        }
        val rolle = if (rolleId.isBlank()) null
        else loadRollenForCurrentUser().firstOrNull { it.id == rolleId }
        loadCurrentUserRole(rolle?.let { roleWithCurrentScreens(it, resolvedArt) })
        org.example.project.service.GlobalManager.triggerSessionConfigReload()
    }
}




